import { isEmpty, isNil } from 'lodash';
import proj4 from 'proj4';
import { Hemisphere } from 'shared/enums';
import { AbortError } from 'shared/errors';
import {
  getUTMCoordinates,
  isCRSDefined,
  isProjectedCrs,
  transformPoint,
} from 'shared/helpers/crs-helpers';
import wkt from 'wkt';
import type { CrossSectionPointData } from '../components/graph-viewer/types';
import type { CrossSectionPoint, Point2D, PreprocessedInputs } from '../types';

/**
 * Checks if an AbortSignal is aborted and throws AbortError if so.
 */
export const checkAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new AbortError('Operation was cancelled');
  }
};

/**
 * Extracts error message from unknown error type.
 */
export const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error);
};

/**
 * Reports progress for segment processing.
 * Calculates progress as a percentage (0-100) based on segment completion.
 */
export const reportProgress = (
  currentSegment: number,
  totalSegments: number,
  onProgress?: (progress: number) => void,
): void => {
  if (!isNil(onProgress) && totalSegments > 0) {
    const progress = Math.round(((currentSegment + 1) / totalSegments) * 100);
    onProgress(progress);
  }
};

/**
 * Detects if coordinates are geographic and sets up transformation to meters.
 *
 * If the CRS is geographic (e.g., WGS84), converts coordinates to UTM for metric calculations.
 * This ensures that buffer distances in meters work correctly with geometric calculations.
 *
 * @param path - Array of 2D coordinates in the original CRS
 * @param buffer - Buffer distance in meters
 * @param epsgCode - EPSG code of the source CRS
 * @returns Preprocessed inputs with transformed coordinates and transformation functions
 * @throws Error if CRS is geographic but transformation to UTM fails
 */
export const preprocessInputsForMetricCalculations = (
  path: Point2D[],
  buffer: number,
  sourceCrs: number,
): PreprocessedInputs => {
  if (isEmpty(path)) {
    throw new Error('Path must be a non-empty array');
  }

  // Check if CRS is already projected (in meters)
  if (isProjectedCrs(sourceCrs)) {
    // Already in projected coordinates (meters), no transformation needed
    return {
      pathInMeters: path,
      bufferInMeters: buffer,
      isProjected: true,
    };
  }

  // Geographic coordinates detected - need to project to UTM for metric calculations
  // Get centroid
  const centerLon = path.reduce((sum, [lon]) => sum + lon, 0) / path.length;
  const centerLat = path.reduce((sum, [, lat]) => sum + lat, 0) / path.length;

  // Use getUTMCoordinates helper to get zone info
  const { zoneNumber, hemisphere } = getUTMCoordinates(
    centerLon,
    centerLat,
    sourceCrs,
  );

  // Validate UTM zone was calculated successfully
  if (zoneNumber === -1 || hemisphere === Hemisphere.UNKNOWN) {
    throw new Error(
      `Failed to determine UTM zone for coordinates. Cannot transform geographic CRS (EPSG:${sourceCrs}) to UTM.`,
    );
  }

  // Transform path to UTM using helper function
  const pathInMeters = path.map(([x, y]) =>
    transformPoint([x, y], sourceCrs, zoneNumber, hemisphere, true),
  );

  return {
    pathInMeters,
    bufferInMeters: buffer,
    isProjected: false,
    utmContext: {
      epsgCode: sourceCrs,
      zoneNumber,
      hemisphere,
    },
  };
};

/**
 * Parses a WKT LINESTRING into an array of 2D coordinates.
 *
 * @param wktString - WKT LINESTRING format string (e.g., "LINESTRING(lng lat, lng lat, ...)")
 * @returns Array of [x, y] coordinate tuples
 * @throws Error if WKT parsing fails or format is invalid
 */
export const parseWktLinestring = (wktString: string): Point2D[] => {
  try {
    const parsed = wkt.parse(wktString);

    if (parsed.type !== 'LineString') {
      throw new Error(`Expected LineString, got ${parsed.type}`);
    }

    // WKT coordinates are [lng, lat] or [x, y]
    const coordinates = parsed.coordinates as number[][];

    if (isNil(coordinates) || coordinates.length < 2) {
      throw new Error('LineString must have at least 2 points');
    }

    return coordinates.map(([x, y]) => [x, y] as Point2D);
  } catch (error) {
    throw new Error(
      `Failed to parse WKT LINESTRING: ${getErrorMessage(error)}`,
    );
  }
};

/**
 * Parses EPSG code from WKT (Well-Known Text) string using the wkt library.
 *
 * @param wktString - WKT coordinate reference system string
 * @returns EPSG code as number, or null if not found
 */
export const parseCrsFromWkt = (
  wktString: string | undefined,
): number | null => {
  if (isNil(wktString)) {
    return null;
  }

  try {
    // Use wkt library to parse the WKT string.
    const parsed = wkt.parse(wktString);

    // The parsed object has an 'authority' property with EPSG code.
    if (parsed?.authority) {
      const epsgCode = parseInt(parsed.authority, 10);

      if (!isNaN(epsgCode)) {
        return epsgCode;
      }
    }

    // Try to extract EPSG code from ID field in WKT.
    const epsgMatch = wktString.match(/ID\["EPSG",(\d+)\]/i);
    if (epsgMatch) {
      const epsgCode = parseInt(epsgMatch[1], 10);

      if (!isNaN(epsgCode)) {
        return epsgCode;
      }
    }

    // Fallback: try UTM zone pattern.
    const utmMatch = wktString.match(/UTM\s+zone\s+(\d+)([NS])/i);
    if (utmMatch) {
      const zone = parseInt(utmMatch[1], 10);
      const hemisphere = utmMatch[2].toUpperCase();
      return hemisphere === 'N' ? 32600 + zone : 32700 + zone;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Reprojects coordinates from one CRS to another using proj4.
 * Note: If CRS are the same, returns coordinates unchanged.
 *
 * @param coords - Array of [x, y] coordinates in source CRS
 * @param fromEpsg - Source EPSG code (e.g., 4326 for WGS84)
 * @param toEpsg - Target EPSG code
 * @returns Array of reprojected [x, y] coordinates
 * @throws Error if reprojection fails or CRS is not defined
 */
export const reprojectCoordinates = (
  coords: Point2D[],
  fromEpsg: number,
  toEpsg: number,
): Point2D[] => {
  try {
    // If source and target are the same, no reprojection needed.
    if (fromEpsg === toEpsg) {
      return coords;
    }

    // Validate that both CRS are defined.
    if (!isCRSDefined(fromEpsg)) {
      throw new Error(
        `Source CRS EPSG:${fromEpsg} is not defined. Register it first using registerCRSFromInfo.`,
      );
    }

    if (!isCRSDefined(toEpsg)) {
      throw new Error(
        `Target CRS EPSG:${toEpsg} is not defined. Register it first using registerCRSFromInfo.`,
      );
    }

    const fromProj = `EPSG:${fromEpsg}`;
    const toProj = `EPSG:${toEpsg}`;

    return coords.map(([x, y]) => {
      const [newX, newY] = proj4(fromProj, toProj, [x, y]);
      return [newX, newY] as Point2D;
    });
  } catch (error) {
    throw new Error(
      `Failed to reproject from EPSG:${fromEpsg} to EPSG:${toEpsg}: ${getErrorMessage(error)}`,
    );
  }
};

/**
 * Transforms CrossSectionPoint array from extractor to CrossSectionPointData format for viewer.
 *
 * @param points - Array of cross-section points from extractor
 * @returns Formatted data for GraphViewer component with positions and colors as typed arrays
 */
export const transformCrossSectionPoints = (
  points: CrossSectionPoint[],
): CrossSectionPointData => {
  const count = points.length;

  // Positions: [mileage, x, y, z] per point (4 values per point)
  const positions = new Float32Array(count * 4);

  // Colors: [r, g, b] per point (3 values per point)
  const colors = new Uint8Array(count * 3);

  points.forEach((pt, i) => {
    const posIdx = i * 4;
    const colorIdx = i * 3;

    // Position data
    positions[posIdx] = pt.mileage; // mileage: distance along path
    positions[posIdx + 1] = pt.x; // x: COPC X coordinate
    positions[posIdx + 2] = pt.y; // y: COPC Y coordinate
    positions[posIdx + 3] = pt.z; // z: COPC Z coordinate (elevation)

    // Color data
    colors[colorIdx] = pt.r; // Red
    colors[colorIdx + 1] = pt.g; // Green
    colors[colorIdx + 2] = pt.b; // Blue
  });

  return {
    positions,
    colors,
    count,
  };
};
